import React, { useState, useEffect } from "react";
import {
  Button, Box, Typography, IconButton, Snackbar, Alert, Chip, Tooltip, Divider, Popover
} from "@mui/material";
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Close as CloseIcon } from '@mui/icons-material';
import RemoveIcon from '@mui/icons-material/Remove';
import BusinessIcon from '@mui/icons-material/Business';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PersonIcon from '@mui/icons-material/Person';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import DescriptionIcon from '@mui/icons-material/Description';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Checkbox from '@mui/material/Checkbox';
import { useHistory, useParams } from "react-router-dom";
import axios from "../../axios-order";

// ---------------------------------------------------------------------------
// Design tokens — single source of truth for the color system used below.
// ---------------------------------------------------------------------------
const COLORS = {
  headerBg: '#03406d',        // your topbar color
  headerBgSoft: '#0b5a91',    // lighter, more saturated blue — gradient end
  headerText: '#eaf3fa',
  rowAlt: '#eef5fb',           // pale blue tint
  rowHover: '#dbeaf5',
  rowSelected: '#c3ddf0',
  border: '#d7e4ee',
  primary: '#0f6fb0',          // brighter mid-blue for icons/links/buttons
  primarySoft: '#dbeaf5',
};

const STATUS_STYLE = {
  Pass: { bg: '#dcfce7', color: '#15803d' },
  Passed: { bg: '#dcfce7', color: '#15803d' },
  Success: { bg: '#dcfce7', color: '#15803d' },
  Amended: { bg: '#dcfce7', color: '#15803d' },
  Fail: { bg: '#fee2e2', color: '#b91c1c' },
  Failed: { bg: '#fee2e2', color: '#b91c1c' },
  Rejected: { bg: '#fee2e2', color: '#b91c1c' },
  'In-Progress': { bg: '#fef3c7', color: '#92400e' },
  Pending: { bg: '#fef3c7', color: '#92400e' },
  'Missing in Accounts': { bg: '#fef3c7', color: '#92400e' },
  Uploads: { bg: '#e0e7ff', color: '#3730a3' },
};
const statusChipSx = (status) => {
  const s = STATUS_STYLE[status] || { bg: '#f1f5f9', color: '#475569' };
  return { backgroundColor: s.bg, color: s.color, fontWeight: 600, fontSize: '11.5px', height: 22 };
};

const thSx = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  background: `linear-gradient(180deg, ${COLORS.headerBg} 0%, ${COLORS.headerBgSoft} 100%)`,
  color: COLORS.headerText,
  fontWeight: 600,
  fontSize: '12px',
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
  padding: '8px 10px',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  borderBottom: `2px solid ${COLORS.headerBg}`,
};

const tdSx = { padding: '7px 10px', fontSize: '13px', color: '#334155', borderBottom: `1px solid ${COLORS.border}` };

const rowSx = (idx, selected) => ({
  backgroundColor: selected ? COLORS.rowSelected : (idx % 2 === 0 ? '#ffffff' : COLORS.rowAlt),
  transition: 'background-color 0.12s ease',
});

// ---------------------------------------------------------------------------
// Category chips (replaces the old large card grid)
// ---------------------------------------------------------------------------
const INVOICE_CATEGORY_TABS = [
  { value: 'b2b', label: 'B2B, SEZ, DE', Icon: StorefrontIcon, color: '#1565c0', group: 'original' },
  { value: 'b2cl', label: 'B2CL', Icon: PeopleAltIcon, color: '#6a1b9a', group: 'original' },
  { value: 'b2cs', label: 'B2CS', Icon: PersonIcon, color: '#00695c', group: 'original' },
  { value: 'cdnr', label: 'CDNR', Icon: NoteAddIcon, color: '#b45309', group: 'original' },
  { value: 'cdnur', label: 'CDNUR', Icon: DescriptionIcon, color: '#b71c1c', group: 'original' },
  { value: 'exp', label: 'EXP', Icon: FlightTakeoffIcon, color: '#2e7d32', group: 'original' },
  // { value: 'exempted', label: 'EXPA', Icon: VerifiedUserIcon, color: '#bf360c', group: 'original' },
  // ---- Amendment categories: browsable as their own tabs, not just a per-row action ----
  { value: 'b2ba', label: 'B2BA', Icon: HistoryEduIcon, color: '#0369a1', group: 'amendment' },
  { value: 'b2cla', label: 'B2CLA', Icon: HistoryEduIcon, color: '#7e22ce', group: 'amendment' },
  { value: 'b2csa', label: 'B2CSA', Icon: HistoryEduIcon, color: '#0f766e', group: 'amendment' },
  { value: 'cdnra', label: 'CDNRA', Icon: HistoryEduIcon, color: '#c2410c', group: 'amendment' },
  { value: 'cdnura', label: 'CDNURA', Icon: HistoryEduIcon, color: '#be123c', group: 'amendment' },
  { value: 'expa', label: 'EXPA', Icon: VerifiedUserIcon, color: '#bf360c', group: 'amendment' },
];

const CUSTOMER_COL_TABS = ['b2b', 'b2cl', 'cdnr', 'exp', 'b2ba', 'b2cla', 'cdnra'];

const AMENDMENT_TYPE_META = {
  b2ba: { label: 'B2BA Amendment', short: 'B2BA', color: '#1565c0', bg: '#e8f4fd' },
  b2cla: { label: 'B2CLA Amendment', short: 'B2CLA', color: '#6a1b9a', bg: '#f3e8fd' },
  b2csa: { label: 'B2CSA Amendment', short: 'B2CSA', color: '#0f766e', bg: '#e6f4f3' },
  cdnra: { label: 'CDNRA Amendment', short: 'CDNRA', color: '#c2410c', bg: '#fdece3' },
  cdnura: { label: 'CDNURA Amendment', short: 'CDNURA', color: '#be123c', bg: '#fce4ea' },
  expa: { label: 'EXPA Amendment', short: 'EXPA', color: '#2e7d32', bg: '#e8f5e9' },
};

const normalizeFileType = (value) => String(value || '').trim().toLowerCase();
const AMENDMENT_FILE_TYPES = Object.keys(AMENDMENT_TYPE_META);
const isAmendmentFileType = (value) => AMENDMENT_FILE_TYPES.includes(normalizeFileType(value));
const getAmendmentMeta = (value) => AMENDMENT_TYPE_META[normalizeFileType(value)];

// Unique key for an amendment row — the API doesn't return an id, so we
// derive one from GSTIN + invoice number.
const getAmendmentRowKey = (row) => `${row.cust_gstin || ''}_${row.invoice_no || ''}`;

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------
export default function Gstr1aFilingLogReport() {
  const history = useHistory();
  const { reconId } = useParams();

  const [gstr1adata, setgstr1adata] = useState([]);

  // ---- Selective invoice (B2B/CDNR/etc.) — now a single merged view ----
  const [selectedGstr1aLogId, setSelectedGstr1aLogId] = useState(null);
  const [showLogInvoiceView, setShowLogInvoiceView] = useState(false);
  const [logInvoiceActiveTab, setLogInvoiceActiveTab] = useState('b2b');
  const [logInvoiceData, setLogInvoiceData] = useState([]);
  const [logInvoiceLoading, setLogInvoiceLoading] = useState(false);
  const [logInvoiceSearchQuery, setLogInvoiceSearchQuery] = useState('');
  const [selectedLogInvoiceIds, setSelectedLogInvoiceIds] = useState([]);
  const [isSubmittingLogCardSelection, setIsSubmittingLogCardSelection] = useState(false);
  const [isSavingLogInvoiceSelection, setIsSavingLogInvoiceSelection] = useState(false);
  const [logInvoiceSelectOptions, setLogInvoiceSelectOptions] = useState([]);

  // ---- Amendment (b2ba / b2cla / expa) table view ----
  const [showAmendmentView, setShowAmendmentView] = useState(false);
  const [amendmentFileType, setAmendmentFileType] = useState('');
  const [amendmentData, setAmendmentData] = useState([]);
  const [amendmentLoading, setAmendmentLoading] = useState(false);
  // Single-select only — one amendment record can be actioned at a time.
  const [selectedAmendmentKey, setSelectedAmendmentKey] = useState(null);
  const [isSubmittingAmendment, setIsSubmittingAmendment] = useState(false);

  const [amendmentId, setAmendmentId] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // ---- "-" icon (shown when amended_selected is true): click opens a small
  // confirm popover; confirming sends amended_selected: false so the row
  // becomes editable again.
  const [discardingRowKey, setDiscardingRowKey] = useState(null);
  const [discardAnchorEl, setDiscardAnchorEl] = useState(null);
  const [discardTargetRow, setDiscardTargetRow] = useState(null);

  useEffect(() => {
    if (reconId) localStorage.setItem('selected_log_id', reconId);
    getgstr1aLogReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconId]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((s) => ({ ...s, open: false }));
  };

  // ---------------------------------------------------------------------
  // getgstr1aLogReport: fetches the filing runs list, then — WITHOUT any
  // eye-icon click — immediately calls the invoice API
  // (/gstr1a/gstr1a_filing_data via handleOpenLogInvoiceView -> fetchLogInvoices)
  // and displays the data straight into the UI.
  // ---------------------------------------------------------------------
  const getgstr1aLogReport = () => {
    const gstr1_recon_id = reconId || localStorage.getItem('selected_log_id');
    if (!gstr1_recon_id) return;
    axios
      .get(`/gstr1_log_details_via_gstr1a/${gstr1_recon_id}`)
      .then((res) => {
        const records = res.data.records
          ? Array.isArray(res.data.records) ? res.data.records : [res.data.records]
          : [];
        setgstr1adata(records);

        // ---- Directly hit the invoice API and display it — no click needed ----
        if (records.length > 0) {
          handleOpenLogInvoiceView(records[0].log_id);
        }
      })
      .catch((err) => {
        console.error('Error fetching data:', err);
        setgstr1adata([]);
      });
  };

  const filteredLogInvoiceData = logInvoiceData.filter((row) => {
    const q = logInvoiceSearchQuery.toLowerCase();
    if (!q) return true;
    return (
      String(row.cust_gstin || '').toLowerCase().includes(q) ||
      String(row.cust_name || '').toLowerCase().includes(q) ||
      String(row.invoice_no || '').toLowerCase().includes(q) ||
      String(row.invoice_date || '').toLowerCase().includes(q) ||
      String(row.invoice_type || '').toLowerCase().includes(q) ||
      String(row.status || '').toLowerCase().includes(q)
    );
  });

  const fetchLogInvoices = (tabType, log_id) => {
    setLogInvoiceLoading(true);
    setLogInvoiceData([]);
    axios
      .post(`/gstr1a/gstr1a_filing_data`, {
        business_entity_id: parseInt(localStorage.getItem("business_entity_id")),
        log_id: log_id,
        file_type: tabType,
        is_missing_in_books: false,
      })
      .then((res) => {
        setLogInvoiceData(res.data.records || []);
        setLogInvoiceSelectOptions(res.data.select_option || []);
      })
      .catch((err) => console.log(err))
      .finally(() => setLogInvoiceLoading(false));
  };

  // Opens the invoice view AND loads the default (first) category immediately.
  // Now called automatically from getgstr1aLogReport as well as available
  // for programmatic re-use elsewhere if needed.
  const handleOpenLogInvoiceView = (log_id) => {
    setSelectedGstr1aLogId(log_id);
    setShowLogInvoiceView(true);
    setLogInvoiceSearchQuery('');
    setSelectedLogInvoiceIds([]);
    const defaultTab = INVOICE_CATEGORY_TABS[0].value;
    setLogInvoiceActiveTab(defaultTab);
    fetchLogInvoices(defaultTab, log_id);
  };

  const handleCloseLogInvoiceView = () => {
    setShowLogInvoiceView(false);
    setLogInvoiceData([]);
    setLogInvoiceSearchQuery('');
    setSelectedGstr1aLogId(null);
    setSelectedLogInvoiceIds([]);
  };

  // Switching category is a single click on a chip — no intermediate screen.
  const handleCategoryChipClick = (tabValue) => {
    if (tabValue === logInvoiceActiveTab) return;
    setLogInvoiceActiveTab(tabValue);
    setLogInvoiceSearchQuery('');
    setSelectedLogInvoiceIds([]);
    fetchLogInvoices(tabValue, selectedGstr1aLogId);
  };

  const handleSubmitLogCardSelection = () => {
    setIsSubmittingLogCardSelection(true);
    axios
      .post(`/gstr1a_run_selective_invoices_filing_report`, {
        business_entity_id: parseInt(localStorage.getItem("business_entity_id")),
        log_id: selectedGstr1aLogId,
        gstin: localStorage.getItem('gst'),
        is_only_selected: true
      })
      .then((res) => {
        if (res.data.status) {
          setSnackbar({ open: true, message: res.data.message, severity: 'success' });
          handleCloseLogInvoiceView();
          getgstr1aLogReport();
        } else {
          setSnackbar({ open: true, message: res.data.message, severity: 'error' });
        }
      })
      .catch((err) => {
        setSnackbar({ open: true, message: err?.response?.data?.message || 'An error occurred', severity: 'error' });
      })
      .finally(() => setIsSubmittingLogCardSelection(false));
  };

  const handleSaveLogInvoiceSelection = () => {
    if (logInvoiceData.length === 0) {
      setSnackbar({ open: true, message: "No records to save.", severity: 'info' });
      return;
    }
    setIsSavingLogInvoiceSelection(true);

    axios
      .post(`/gstr1a/save_gstr1a_filing_data`, {
        business_entity_id: parseInt(localStorage.getItem("business_entity_id")),
        file_type: logInvoiceActiveTab,
        log_id: selectedGstr1aLogId,
        records: logInvoiceData,
      })
      .then((res) => {
        setSnackbar({ open: true, message: res.data.message || 'Saved successfully', severity: 'success' });
        fetchLogInvoices(logInvoiceActiveTab, selectedGstr1aLogId);
      })
      .catch((err) => {
        setSnackbar({ open: true, message: err?.response?.data?.message || 'An error occurred', severity: 'error' });
      })
      .finally(() => setIsSavingLogInvoiceSelection(false));
  };

  const handleLogInvoiceFileTypeChange = (rowKey, newValue) => {
    setLogInvoiceData((prev) =>
      prev.map((row, i) => {
        const matches = row.voucher_id != null ? row.voucher_id === rowKey : i === rowKey;
        return matches ? { ...row, file_type: newValue } : row;
      })
    );
  };

  // ---- Amendment view: fetch real data from the same endpoint, flagged is_missing_in_books ----
  const handleProceedToAmendment = (fileType, amendmentId) => {
    setAmendmentFileType(fileType);
    setAmendmentId(amendmentId);
    setShowAmendmentView(true);
    setSelectedAmendmentKey(null);
    setAmendmentData([]);
    setAmendmentLoading(true);

    axios
      .post(`/gstr1a/gstr1a_filing_data`, {
        business_entity_id: parseInt(localStorage.getItem("business_entity_id")),
        log_id: selectedGstr1aLogId,
        file_type: fileType,
        is_missing_in_books: true,
        amended_id: amendmentId,
      })
      .then((res) => {
        setAmendmentData(res.data.records || []);
      })
      .catch((err) => {
        console.log(err);
        setSnackbar({ open: true, message: err?.response?.data?.message || 'Failed to load amendment records', severity: 'error' });
      })
      .finally(() => setAmendmentLoading(false));
  };

  // ---- Row dropdown switched to an ORIGINAL file type (b2b, b2cl, ...) ----
  // Mirrors handleProceedToAmendment, but simply takes the user to that
  // original category tab instead of opening the amendment table.
  const handleProceedToOriginal = (fileType) => {
    const tabValue = normalizeFileType(fileType);
    if (!tabValue) return;
    handleCategoryChipClick(tabValue);
  };

  const handleBackFromAmendment = () => {
    setShowAmendmentView(false);
    setAmendmentFileType('');
    setAmendmentId(null);
    setAmendmentData([]);
    setAmendmentLoading(false);
    setSelectedAmendmentKey(null);
  };

  // Only one amendment record may be selected at a time — block and warn otherwise.
  const handleAmendmentCheckbox = (rowKey) => {
    if (selectedAmendmentKey === rowKey) {
      setSelectedAmendmentKey(null);
      return;
    }
    if (selectedAmendmentKey) {
      setSnackbar({
        open: true,
        message: 'Only one record can be selected at a time. Please deselect the current record first.',
        severity: 'warning',
      });
      return;
    }
    setSelectedAmendmentKey(rowKey);
  };

  const handleSubmitAmendment = () => {
    if (!selectedAmendmentKey) {
      setSnackbar({ open: true, message: 'Select a record to submit.', severity: 'info' });
      return;
    }

    const selectedRecord = amendmentData.find(
      (row) => getAmendmentRowKey(row) === selectedAmendmentKey
    );

    if (!selectedRecord) {
      setSnackbar({ open: true, message: 'Selected record not found.', severity: 'error' });
      return;
    }

    setIsSubmittingAmendment(true);

    axios
      .post(`/gstr1a/update_amended`, {
        business_entity_id: parseInt(localStorage.getItem("business_entity_id")),
        log_id: selectedGstr1aLogId,
        file_type: amendmentFileType,
        gstin: localStorage.getItem('gst'),
        amendment_id: amendmentId,
        records: [{ ...selectedRecord, amended_selected: true }],
      })
      .then((res) => {
        if (res.data.status) {
          setSnackbar({ open: true, message: res.data.message, severity: 'success' });
          handleBackFromAmendment();
          fetchLogInvoices(logInvoiceActiveTab, selectedGstr1aLogId);
        } else {
          setSnackbar({ open: true, message: res.data.message, severity: 'error' });
        }
      })
      .catch((err) => {
        setSnackbar({ open: true, message: err?.response?.data?.message || 'An error occurred', severity: 'error' });
      })
      .finally(() => setIsSubmittingAmendment(false));
  };
  const handleBackToReporting = () => {
    history.push('/ReportingGTR1A'); // adjust to your actual Reporting route
  };

  // "-" click opens the confirm popover (doesn't call the API yet).
  const handleOpenDiscardPopover = (event, row) => {
    setDiscardAnchorEl(event.currentTarget);
    setDiscardTargetRow(row);
  };

  const handleCloseDiscardPopover = () => {
    setDiscardAnchorEl(null);
    setDiscardTargetRow(null);
  };

  // Confirm button in the popover -> tell the backend this row is no longer
  // amended-selected, then refresh so the dropdown becomes editable again.
  const handleConfirmDiscard = () => {
    const row = discardTargetRow;
    if (!row) return;
    const rowKey = row.voucher_id ?? row.id;
    setDiscardingRowKey(rowKey);

    axios
      .post(`/gstr1a/update_amended`, {
        business_entity_id: parseInt(localStorage.getItem("business_entity_id")),
        log_id: selectedGstr1aLogId,
        file_type: logInvoiceActiveTab,
        gstin: localStorage.getItem('gst'),
        records: [{ ...row, amended_selected: false }],
      })
      .then((res) => {
        setSnackbar({
          open: true,
          message: res.data?.message || 'Selection updated',
          severity: res.data?.status === false ? 'error' : 'success',
        });
        fetchLogInvoices(logInvoiceActiveTab, selectedGstr1aLogId);
      })
      .catch((err) => {
        setSnackbar({ open: true, message: err?.response?.data?.message || 'Failed to update selection', severity: 'error' });
      })
      .finally(() => {
        setDiscardingRowKey(null);
        handleCloseDiscardPopover();
      });
  };

  const showsCustomerCol = CUSTOMER_COL_TABS.includes(logInvoiceActiveTab);

  return (
    <React.Fragment>
      {/* ---- Compact top bar ---- */}
      <Box
        sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '8px 16px', backgroundColor: '#f5f5f5', borderBottom: `1px solid ${COLORS.border}`,
        }}
      >
        <Tooltip title="Back" arrow>
          <IconButton
            onClick={handleBackToReporting}
            sx={{
              borderRadius: '50%', padding: '5px', color: '#263238', backgroundColor: '#ffffff',
              height: '30px', width: '30px', '&:hover': { backgroundColor: '#455a64', color: '#ffffff' },
              boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
            }}
          >
            <ArrowBackIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </Tooltip>
        <Box display="flex" alignItems="center" gap={0.75}>
          <BusinessIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight="bold">GSTR-1A Filing Log Report</Typography>
        </Box>
      </Box>

      <Box sx={{ px: 2, py: 1.5 }}>
        {/* ---- Log report table (Action/eye-icon column removed — no click needed) ---- */}
        {!showAmendmentView && (
          <Box sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 1.5, overflow: 'hidden', mb: showLogInvoiceView ? 1.5 : 0 }}>
            <Box sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              px: 1.5, py: 0.75, backgroundColor: '#fff', borderBottom: `1px solid ${COLORS.border}`,
            }}>
              <Typography variant="body2" fontWeight={700} sx={{ color: '#1e293b' }}>Filing Runs</Typography>
              <Button size="small" onClick={getgstr1aLogReport} sx={{ fontSize: '12px', minWidth: 'auto', py: 0.25 }}>
                Refresh
              </Button>
            </Box>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thSx}>From Month</th>
                    <th style={thSx}>To Month</th>
                    <th style={thSx}>Financial Year</th>
                    <th style={thSx}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(gstr1adata) && gstr1adata.length > 0 ? (
                    gstr1adata.map((value, idx) => (
                      <tr
                        key={value.log_id}
                        style={rowSx(idx, selectedGstr1aLogId === value.log_id && showLogInvoiceView)}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = COLORS.rowHover)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = rowSx(idx, selectedGstr1aLogId === value.log_id && showLogInvoiceView).backgroundColor)}
                      >
                        <td style={tdSx}>{value.from_month}</td>
                        <td style={tdSx}>{value.to_month}</td>
                        <td style={tdSx}>{value.fiscal_year}</td>
                        <td style={tdSx}><Chip label={value.status} size="small" sx={statusChipSx(value.status)} /></td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8', padding: '20px', fontSize: '13px' }}>No Data Found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Box>
        )}

        {/* ---- Merged category chips + invoice table (auto-loaded, no click required) ---- */}
        {!showAmendmentView && showLogInvoiceView && (
          <Box sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 1.5, overflow: 'hidden' }}>
            {/* Category chip strip */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap',
              px: 1.5, py: 1, backgroundColor: '#fff', borderBottom: `1px solid ${COLORS.border}`,
            }}>
              <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, fontSize: '10.5px', letterSpacing: '0.04em', mr: 0.25 }}>
                ORIGINAL
              </Typography>
              {INVOICE_CATEGORY_TABS.filter((t) => t.group === 'original').map((tab) => {
                const active = tab.value === logInvoiceActiveTab;
                return (
                  <Chip
                    key={tab.value}
                    icon={<tab.Icon sx={{ fontSize: '15px !important', color: active ? '#fff !important' : `${tab.color} !important` }} />}
                    label={tab.label}
                    size="small"
                    onClick={() => handleCategoryChipClick(tab.value)}
                    sx={{
                      fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                      backgroundColor: active ? tab.color : `${tab.color}14`,
                      color: active ? '#fff' : tab.color,
                      border: `1px solid ${active ? tab.color : `${tab.color}55`}`,
                      '&:hover': { backgroundColor: active ? tab.color : `${tab.color}26` },
                    }}
                  />
                );
              })}

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: COLORS.border }} />

              <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, fontSize: '10.5px', letterSpacing: '0.04em', mr: 0.25 }}>
                AMENDMENTS
              </Typography>
              {INVOICE_CATEGORY_TABS.filter((t) => t.group === 'amendment').map((tab) => {
                const active = tab.value === logInvoiceActiveTab;
                return (
                  <Chip
                    key={tab.value}
                    icon={<tab.Icon sx={{ fontSize: '15px !important', color: active ? '#fff !important' : `${tab.color} !important` }} />}
                    label={tab.label}
                    size="small"
                    onClick={() => handleCategoryChipClick(tab.value)}
                    sx={{
                      fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                      backgroundColor: active ? tab.color : `${tab.color}14`,
                      color: active ? '#fff' : tab.color,
                      border: `1px dashed ${active ? tab.color : `${tab.color}66`}`,
                      '&:hover': { backgroundColor: active ? tab.color : `${tab.color}26` },
                    }}
                  />
                );
              })}

              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="text"
                  placeholder="Search..."
                  value={logInvoiceSearchQuery}
                  onChange={(e) => setLogInvoiceSearchQuery(e.target.value)}
                  style={{ padding: '5px 10px', border: `1px solid ${COLORS.border}`, borderRadius: '6px', fontSize: '12.5px', width: '180px', outline: 'none' }}
                />
                <Button
                  size="small" variant="contained" onClick={handleSaveLogInvoiceSelection}
                  disabled={isSavingLogInvoiceSelection}
                  sx={{ fontSize: '12px', py: 0.4 }}
                  startIcon={isSavingLogInvoiceSelection ? <HourglassBottomIcon sx={{ animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} /> : null}
                >
                  {isSavingLogInvoiceSelection ? 'Saving...' : 'Save'}
                </Button>
                {/* <IconButton
                  size="small"
                  onClick={handleCloseLogInvoiceView}
                  sx={{ transition: 'background-color 0.15s ease', '&:hover': { backgroundColor: COLORS.rowSelected } }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton> */}
              </Box>
            </Box>

            {/* Invoice table for active category — no row checkboxes */}
            <div style={{ maxHeight: '440px', overflowY: 'auto', overflowX: 'auto' }}>
              {logInvoiceLoading ? (
                <Box sx={{ textAlign: 'center', py: 4, color: COLORS.primary, fontSize: '13px' }}>Loading invoices...</Box>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={thSx}>File Type</th>
                      <th style={thSx}>GSTIN</th>
                      {showsCustomerCol && <th style={thSx}>Customer Name</th>}
                      <th style={thSx}>Original Document Date</th>
                      <th style={thSx}>Original Document No</th>
                      <th style={thSx}>Invoice No</th>
                      <th style={thSx}>Invoice Date</th>
                      <th style={thSx}>Invoice Type</th>
                      <th style={thSx}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogInvoiceData.length === 0 ? (
                      <tr>
                        <td colSpan={showsCustomerCol ? 7 : 6} style={{ textAlign: 'center', color: '#94a3b8', padding: '24px', fontSize: '13px' }}>
                          {logInvoiceSearchQuery ? 'No matching records found' : 'No records found'}
                        </td>
                      </tr>
                    ) : (
                      filteredLogInvoiceData.map((row, idx) => (
                        <tr
                          key={row.log_id || row.voucher_id || idx}
                          style={rowSx(idx, false)}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = COLORS.rowHover)}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = rowSx(idx, false).backgroundColor)}
                        >
                          <td style={tdSx}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <select
                                value={row.file_type || ''}
                                disabled={row.amended_selected}
                                onChange={(e) => handleLogInvoiceFileTypeChange(row.voucher_id ?? idx, e.target.value)}
                                style={{ padding: '3px 5px', borderRadius: '4px', border: `1px solid ${COLORS.border}`, fontSize: '12.5px', opacity: row.amended_selected ? 0.6 : 1, cursor: row.amended_selected ? 'not-allowed' : 'auto' }}
                              >
                                {Array.from(new Set([row.file_type, ...logInvoiceSelectOptions].filter(Boolean))).map((opt) => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>

                              {/* Arrow appears only for rows whose own file_type is an
                                  amendment type — never blanket for a whole tab. This is
                                  true whether you're browsing an original tab (row was
                                  switched via dropdown) or an amendment tab (row's type
                                  already matches). */}

                                  {/* Arrow only shows on ORIGINAL tabs, for rows whose file_type was
    switched to an amendment type. Hidden while already browsing an
    amendment tab — no need to "proceed" to where you already are. */}
{!isAmendmentFileType(logInvoiceActiveTab) && isAmendmentFileType(row.file_type) && (() => {
  const meta = getAmendmentMeta(row.file_type);
  return (
    <Tooltip title={`Proceed to ${meta?.label}`} arrow>
      <span>
        <IconButton
          size="small"
          disabled={row.amended_selected}
          onClick={() => handleProceedToAmendment(row.file_type, row.id)}
          sx={{
            width: 22, height: 22, backgroundColor: meta?.color, color: '#fff',
            opacity: row.amended_selected ? 0.5 : 1,
            '&:hover': { opacity: row.amended_selected ? 0.5 : 0.85, backgroundColor: meta?.color },
          }}
        >
          <ArrowForwardIcon sx={{ fontSize: 13 }} />
        </IconButton>
      </span>
    </Tooltip>
  );
})()}
                              {/* {isAmendmentFileType(row.file_type) && (() => {
                                const meta = getAmendmentMeta(row.file_type);
                                return (
                                  <Tooltip title={`Proceed to ${meta?.label}`} arrow>
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={row.amended_selected}
                                        onClick={() => handleProceedToAmendment(row.file_type, row.id)}
                                        sx={{
                                          width: 22, height: 22, backgroundColor: meta?.color, color: '#fff',
                                          opacity: row.amended_selected ? 0.5 : 1,
                                          '&:hover': { opacity: row.amended_selected ? 0.5 : 0.85, backgroundColor: meta?.color },
                                        }}
                                      >
                                        <ArrowForwardIcon sx={{ fontSize: 13 }} />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                );
                              })()} */}

                              {/* "-" appears only when the backend has already recorded an
                                  amended_selected choice for this row (merged table only —
                                  not shown on the separate Amendment screen). Clicking it
                                  opens a small confirm popover before calling the API. */}
                              {row.amended_selected && (
                                <Tooltip title="Discard selection" arrow>
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={discardingRowKey === (row.voucher_id ?? row.id)}
                                      onClick={(e) => handleOpenDiscardPopover(e, row)}
                                      sx={{ width: 22, height: 22, backgroundColor: '#fee2e2', color: '#b91c1c', '&:hover': { backgroundColor: '#fecaca' } }}
                                    >
                                      <RemoveIcon sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </Box>
                          </td>
                          <td style={tdSx}>{row.cust_gstin}</td>
                          {showsCustomerCol && <td style={tdSx}>{row.cust_name}</td>}
                          <td style={tdSx}>{row.original_document_date}</td>
                          <td style={tdSx}>{row.original_document_no}</td>

                          <td style={tdSx}>{row.invoice_no}</td>
                          <td style={tdSx}>{row.invoice_date}</td>
                          <td style={tdSx}>{row.invoice_type}</td>
                          <td style={tdSx}><Chip label={row.status} size="small" sx={statusChipSx(row.status)} /></td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </Box>
        )}

        {/* ---- Amendment table (b2ba / b2cla / expa) — single-select only ---- */}
        {showAmendmentView && (
          <Box sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 1.5, overflow: 'hidden' }}>
            <Box sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1.5, py: 1,
              background: `linear-gradient(135deg, ${getAmendmentMeta(amendmentFileType)?.color || COLORS.headerBg} 0%, ${COLORS.headerBg} 100%)`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 30, height: 30, borderRadius: '8px', backgroundColor: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <NoteAddIcon sx={{ color: '#fff', fontSize: 17 }} />
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: '#fff', lineHeight: 1.2 }}>
                    {getAmendmentMeta(amendmentFileType)?.label || 'Amendment'} — Invoice Details
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '11px' }}>
                    {amendmentData.length} record{amendmentData.length !== 1 ? 's' : ''} &middot; {selectedAmendmentKey ? 1 : 0} selected
                  </Typography>
                </Box>
              </Box>
              <Button
                size="small" variant="outlined" onClick={handleBackFromAmendment} startIcon={<ArrowBackIcon sx={{ fontSize: 15 }} />}
                sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.6)', fontSize: '12px', py: 0.25, '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' } }}
              >
                Back
              </Button>
            </Box>

            <div style={{ maxHeight: '440px', overflowY: 'auto', overflowX: 'auto' }}>
              {amendmentLoading ? (
                <Box sx={{ textAlign: 'center', py: 4, color: COLORS.primary, fontSize: '13px' }}>Loading amendment records...</Box>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...thSx, width: 36 }}></th>
                      <th style={thSx}>GSTIN</th>
                      <th style={thSx}>Customer Name</th>
                      <th style={thSx}>Original Document Date</th>
                      <th style={thSx}>Original Document No</th>
                      <th style={thSx}>Invoice Date</th>
                      <th style={thSx}>Invoice No</th>
                      <th style={thSx}>Invoice Type</th>
                      <th style={thSx}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {amendmentData.length === 0 ? (
                      <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8', padding: '24px', fontSize: '13px' }}>No records found</td></tr>
                    ) : (
                      amendmentData.map((row, idx) => {
                        const rowKey = getAmendmentRowKey(row);
                        const checked = selectedAmendmentKey === rowKey;
                        return (
                          <tr
                            key={rowKey || idx}
                            style={rowSx(idx, checked)}
                            onMouseEnter={(e) => { if (!checked) e.currentTarget.style.backgroundColor = COLORS.rowHover; }}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = rowSx(idx, checked).backgroundColor)}
                          >
                            <td style={tdSx}>
                              <Checkbox size="small" checked={checked} onChange={() => handleAmendmentCheckbox(rowKey)} sx={{ p: 0 }} />
                            </td>
                            <td style={tdSx}>{row.cust_gstin}</td>
                            <td style={{ ...tdSx, fontWeight: 500, color: '#1e293b' }}>{row.cust_name || '-'}</td>
                            <td style={{ ...tdSx, color: '#0d47a1', fontWeight: 600 }}>{row.invoice_no}</td>
                            <td style={tdSx}>{row.invoice_date}</td>
                            <td style={tdSx}>{row.original_document_date}</td>
                            <td style={tdSx}>{row.original_document_no}</td>
                            <td style={tdSx}>{row.invoice_type}</td>
                            <td style={tdSx}><Chip label={row.status} size="small" sx={statusChipSx(row.status)} /></td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, px: 1.5, py: 1, borderTop: `1px solid ${COLORS.border}`, backgroundColor: '#fafbfc' }}>
              <Typography variant="caption" sx={{ color: '#64748b', mr: 'auto', fontSize: '11.5px' }}>
                {selectedAmendmentKey ? '1' : '0'} of {amendmentData.length} record{amendmentData.length !== 1 ? 's' : ''} selected
              </Typography>
              <Button variant="outlined" size="small" onClick={handleBackFromAmendment} sx={{ fontSize: '12px', py: 0.3 }}>Cancel</Button>
              <Button
                variant="contained" size="small" color="primary" onClick={handleSubmitAmendment}
                disabled={!selectedAmendmentKey || isSubmittingAmendment}
                sx={{ fontSize: '12px', py: 0.3 }}
                startIcon={isSubmittingAmendment ? <HourglassBottomIcon sx={{ animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} /> : null}
              >
                {isSubmittingAmendment ? 'Submitting...' : 'Submit'}
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Small confirm popover for the "-" icon — one clear action, no extra choices. */}
      <Popover
        open={Boolean(discardAnchorEl)}
        anchorEl={discardAnchorEl}
        onClose={handleCloseDiscardPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 180 }}>
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '12.5px', color: '#1e293b' }}>
            Discard this into originals?
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small" variant="outlined" color="error"
              disabled={discardingRowKey === (discardTargetRow?.voucher_id ?? discardTargetRow?.id)}
              onClick={handleConfirmDiscard}
              sx={{ fontSize: '11.5px', flex: 1, py: 0.3 }}
            >
              Confirm
            </Button>
            <Button size="small" onClick={handleCloseDiscardPopover} sx={{ fontSize: '11.5px', flex: 1, py: 0.3 }}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Popover>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}  